import { useEffect, useMemo, useState } from "react";
import { Search, Target } from "lucide-react";
import { Link } from "react-router-dom";

import { api } from "../../api/client";
import { appraisalName, daysUntil } from "../../utils/appraisal";
import { DueLine, ProgressPill, type AgreedObjective } from "./Objectives";

interface Person {
  employee_id: number;
  employee_name: string;
  site_name: string | null;
  manager_name: string | null;
  review_id: number;
  appraisal_year: number | null;
  meeting_date: string | null;
  objectives: AgreedObjective[];
  count_total: number;
  count_done: number;
  count_need_help: number;
  count_overdue: number;
}

type Filter = "all" | "need_help" | "overdue" | "no_update";

const isOverdue = (o: AgreedObjective) => o.progress !== "done" && (daysUntil(o.due_date) ?? 0) < 0;

/**
 * Admin → Performance → Objectives: everyone's objectives from their newest
 * finished appraisal, so HR can see who needs help or has fallen behind.
 */
export default function ObjectivesOverview() {
  const [people, setPeople] = useState<Person[] | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    void (async () => {
      try {
        setPeople((await api.get<{ people: Person[] }>("/api/objectives/overview")).people);
      } catch {
        setPeople([]);
      }
    })();
  }, []);

  const totals = useMemo(() => {
    const all = (people ?? []).flatMap((p) => p.objectives);
    return {
      objectives: all.length,
      done: all.filter((o) => o.progress === "done").length,
      needHelp: all.filter((o) => o.progress === "need_help").length,
      overdue: all.filter(isOverdue).length,
      noUpdate: all.filter((o) => o.progress == null).length,
    };
  }, [people]);

  if (people == null) return <p className="text-sm text-gray-400">Loading objectives...</p>;

  const q = query.trim().toLowerCase();
  const shown = people.filter((p) => {
    if (q && !`${p.employee_name} ${p.manager_name ?? ""} ${p.site_name ?? ""}`.toLowerCase().includes(q)) return false;
    if (filter === "need_help") return p.objectives.some((o) => o.progress === "need_help");
    if (filter === "overdue") return p.objectives.some(isOverdue);
    if (filter === "no_update") return p.objectives.some((o) => o.progress == null);
    return true;
  });

  const chips: { key: Filter; label: string; count: number }[] = [
    { key: "all", label: "Everyone", count: people.length },
    { key: "need_help", label: "Need help", count: totals.needHelp },
    { key: "overdue", label: "Past their date", count: totals.overdue },
    { key: "no_update", label: "No update yet", count: totals.noUpdate },
  ];

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs text-blue-900">
        <p className="flex items-center gap-1.5 text-sm font-semibold"><Target size={14} aria-hidden /> Objectives for the year</p>
        <p className="mt-1">
          The objectives agreed in each person's latest finished appraisal. Employees update their own progress, and managers update it
          at catch-ups. {totals.done} of {totals.objectives} objectives are done.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {chips.map((c) => (
          <button key={c.key} type="button" onClick={() => setFilter(c.key)} aria-pressed={filter === c.key}
            className={`rounded-full px-3 py-1 text-xs font-semibold ${filter === c.key ? "bg-pme-blue text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
            {c.label} ({c.count})
          </button>
        ))}
        <label className="flex w-full items-center sm:ml-auto sm:w-auto sm:min-w-[240px] gap-2 rounded-lg border border-gray-200 bg-white px-3">
          <Search size={14} className="text-gray-400" aria-hidden />
          <span className="sr-only">Find a person, manager or site</span>
          <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a person, manager or site"
            className="min-h-[36px] w-full bg-transparent text-sm outline-none" />
        </label>
      </div>

      {people.length === 0 ? (
        <p className="rounded-xl border border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
          No objectives yet. They appear here once an appraisal is finished.
        </p>
      ) : shown.length === 0 ? (
        <p className="text-sm text-gray-500">Nobody matches.</p>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {shown.map((p) => (
            <div key={p.employee_id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{p.employee_name}</p>
                  <p className="text-xs text-gray-500">
                    {[appraisalName(p.appraisal_year), p.site_name, p.manager_name ? `Manager: ${p.manager_name}` : null].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <span className="text-xs font-semibold text-gray-700">{p.count_done} of {p.count_total} done</span>
              </div>
              <ul className="mt-2 space-y-2">
                {p.objectives.map((o) => (
                  <li key={o.id} className="rounded-lg bg-gray-50 px-3 py-2">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <p className="text-sm text-gray-800">{o.objective}</p>
                      <ProgressPill progress={o.progress} />
                    </div>
                    <p className="mt-0.5 text-xs"><DueLine due={o.due_date} done={o.progress === "done"} /></p>
                    {o.progress_note && <p className="mt-0.5 text-xs text-gray-600">Latest: {o.progress_note}</p>}
                  </li>
                ))}
              </ul>
              <Link to={`/review?review=${p.review_id}`} className="mt-2 inline-block text-xs font-semibold text-pme-blue hover:underline">
                Open the appraisal
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
